import {
  BREAKDOWN_LABELS,
  batchDocState,
  batchRowStateLabel,
  batchStatusPhrase,
  deriveDocState,
  docStateLabel,
  type BatchRowState,
  type WorkabilityStatus,
} from "@capture/shared";
import { formatDocumentStatus } from "@/lib/formatters";
import styles from "./BatchStatusBadge.module.css";

// F-CAPTURE-009 — single-sourced in @capture/shared so the API can sort by the
// exact phrase this badge renders. Re-exported for existing importers.
export { batchDocState, workItemStatus } from "@capture/shared";

/**
 * Six aggregate statuses computed by the API
 * (`apps/api/src/services/batch-aggregate-status.ts`).
 *
 * Kept as a string-literal union here instead of importing the API type
 * because the web app intentionally doesn't import server modules — same
 * shape, separate source of truth. If a seventh state is ever added it
 * must be added in both places (the API tests guard the type contract).
 *
 * R-154 — `Exporting` distinguishes "some doc at currentStep=export but
 * not yet exported" from "Keying in Progress" so BatchDetailPage no
 * longer renders contradicting header/breakdown/pipeline-card labels.
 *
 * F-074 — `Awaiting Classification` added for the classify-parked bucket.
 * This file is the single web-side source of truth for `AggregateStatus`;
 * `hooks/useBatches.ts` imports the type from here rather than duplicating
 * it. The API (`apps/api/src/services/batch-aggregate-status.ts`) keeps its
 * own copy intentionally, per the note above — that's the pair to keep in
 * sync, not this one.
 */
export type AggregateStatus =
  | "Pending"
  | "In Progress"
  | "Ready for Keying"
  | "Keying in Progress"
  | "Exporting"
  | "Complete"
  | "Awaiting Classification";

export interface StageBreakdown {
  ready: number;
  scanning: number;
  keying: number;
  /** R-154 — docs at currentStep=export but not yet status=exported. */
  exporting: number;
  complete: number;
  error: number;
  /** F-074 — classify-parked docs (operator-actionable). Optional: pre-F-074 fixtures/responses. */
  awaitingClassify?: number;
  total: number;
}

interface BatchStatusBadgeProps {
  /**
   * The batch's per-stage doc breakdown. The badge collapses it to the canonical
   * 4-state model (F-048) plus `awaiting_classify` (F-074). Absent / empty
   * (total 0) → renders nothing.
   */
  breakdown?: StageBreakdown | null;
  /**
   * F-074 — when supplied, the badge renders ONE composed phrase
   * ("<state> — <workability>") instead of the bare state word, replacing the
   * separate `batch-work-item` span that used to sit next to it.
   */
  workability?: WorkabilityStatus;
  className?: string;
}

const VARIANT_BY_STATE: Record<BatchRowState, string> = {
  ready: styles["badge--ready"]!,
  // active mid-flight tone (keying/scanning/exporting all read as "In progress")
  busy: styles["badge--keying"]!,
  error: styles["badge--error"]!,
  complete: styles["badge--complete"]!,
  // F-074 — ready tone: classify-parked docs are operator-actionable, not stuck.
  awaiting_classify: styles["badge--ready"]!,
};


/**
 * Batch status badge — the canonical row-state vocabulary (F-048/F-074),
 * single-sourced via `batchRowStateLabel`/`batchStatusPhrase`, so the Batches list
 * reads the SAME states as the Queue Dashboard instead of a divergent string
 * set. When `workability` is supplied, renders the ONE composed phrase
 * ("<state> — <workability>") replacing the old stacked badge + separate
 * work-item span. Both BatchListPage and BatchDetailPage (F-085) pass
 * `workability` so header and list rows read identically.
 */
export function BatchStatusBadge({ breakdown, workability, className }: BatchStatusBadgeProps) {
  if (!breakdown || breakdown.total === 0) return null;
  const state = batchDocState(breakdown);
  const label = workability
    ? batchStatusPhrase(state, workability)
    : batchRowStateLabel(state);

  return (
    <span
      className={`${styles["badge"]!} ${VARIANT_BY_STATE[state]} ${className ?? ""}`.trim()}
      data-testid="batch-status-badge"
      data-state={state}
    >
      {label}
    </span>
  );
}

/**
 * F-085 — per-DOCUMENT state badge for doc rows (BatchDetailPage table).
 * Collapses the raw `documents.status` onto the shared 4-state vocabulary via
 * `deriveDocState`/`docStateLabel` and reuses the SAME tone classes as the
 * batch-level badge, so a doc row can never show a word ("pending",
 * "processing", …) that the rest of the product doesn't speak. Voided docs
 * (split/merged/deleted) are not live work — they keep their own word in the
 * muted pending tone.
 */
export function DocStateBadge({ status }: { status: string }) {
  const state = deriveDocState(status);
  const isVoided = state === "voided";
  const variant = isVoided ? styles["badge--pending"]! : VARIANT_BY_STATE[state];
  const label = isVoided ? formatDocumentStatus(status) : docStateLabel(state);

  return (
    <span
      className={`${styles["badge"]!} ${variant}`}
      data-testid="doc-state-badge"
      data-state={state}
    >
      {label}
    </span>
  );
}

/**
 * Per-stage breakdown line — "5 available · 0 scanning · 0 error".
 *
 * Renders inline under the batch name on list rows. Hides zero-count
 * buckets except for `available` (always shown — keyers want to confirm
 * "0 available" too) and `error` (always shown when > 0).
 *
 * DE-1545 F-060 — the segment WORDS come from the canonical `BREAKDOWN_LABELS`
 * so the breakdown can never drift from the pill / workability vocabulary. The
 * leading `ready` data bucket renders as "available" so the word "ready" is
 * never reused for a third concept on the row.
 *
 * Skips rendering entirely when the batch has zero docs — nothing
 * useful to convey.
 */
interface BatchStageBreakdownProps {
  /** Optional — pre-WU-8 batch fixtures / API responses that don't include it just render nothing. */
  breakdown?: StageBreakdown | null;
  className?: string;
}

export function BatchStageBreakdown({
  breakdown,
  className,
}: BatchStageBreakdownProps) {
  // Pre-WU-8 fixtures and any transitional API response lacking the field
  // simply hide the line rather than crashing the row.
  if (!breakdown || breakdown.total === 0) return null;

  // Always show the available bucket (even at zero — keyers verify "nothing
  // pickable yet"). Show others only when non-zero so the line stays short on
  // small batches. Labels are single-sourced from BREAKDOWN_LABELS (F-060).
  const awaitingClassify = breakdown.awaitingClassify ?? 0;

  const segments: { label: string; value: number; variant?: "ready" | "error" }[] =
    [{ label: BREAKDOWN_LABELS.available, value: breakdown.ready, variant: "ready" }];

  // F-074 — classify-parked docs get their own segment, right after available.
  if (awaitingClassify > 0)
    segments.push({ label: BREAKDOWN_LABELS.awaitingClassify, value: awaitingClassify });
  if (breakdown.scanning > 0)
    segments.push({ label: BREAKDOWN_LABELS.scanning, value: breakdown.scanning });
  if (breakdown.keying > 0)
    segments.push({ label: BREAKDOWN_LABELS.keying, value: breakdown.keying });
  // R-154 — keep `exporting` distinct from `keying` so the breakdown
  // mirrors the header badge and pipeline-progress cards.
  if (breakdown.exporting > 0)
    segments.push({ label: BREAKDOWN_LABELS.exporting, value: breakdown.exporting });
  if (breakdown.complete > 0)
    segments.push({ label: BREAKDOWN_LABELS.complete, value: breakdown.complete });
  if (breakdown.error > 0)
    segments.push({ label: BREAKDOWN_LABELS.error, value: breakdown.error, variant: "error" });

  return (
    <div
      className={`${styles["breakdown"]!} ${className ?? ""}`.trim()}
      data-testid="batch-stage-breakdown"
    >
      {segments.map((seg, i) => (
        <span key={seg.label} style={{ display: "inline-flex", gap: 4 }}>
          {i > 0 && <span className={styles["breakdownSeparator"]!}>·</span>}
          <span
            className={`${styles["breakdownItem"]!} ${
              seg.variant === "ready"
                ? styles["breakdownItem--ready"]!
                : seg.variant === "error"
                  ? styles["breakdownItem--error"]!
                  : ""
            }`.trim()}
          >
            {seg.value} {seg.label}
          </span>
        </span>
      ))}
    </div>
  );
}
